import { Texture, Spritesheet } from 'pixi.js';
import { Spine } from '@esotericsoftware/spine-pixi-v8';
import { AssetService, GameAssetManifest } from './loaders/AssetService';

/**
 * PixiBundler
 * ----------------------------------------------------
 * Main asset facade used by games.
 * Wraps AssetService and provides per-game isolation.
 */
export class PixiBundler {
  private assetService: AssetService;
  private gameSlug: string;
  private manifest: GameAssetManifest | null = null;

  // Atlas cache (per game)
  private atlases = new Map<string, Spritesheet>();

  constructor(gameSlug: string, baseUrl: string = '') {
    this.gameSlug = gameSlug;
    this.assetService = AssetService.getInstance(baseUrl);
  }

  /* --------------------------------------------------
   * INITIALIZATION
   * -------------------------------------------------- */

  async initialize(manifest?: GameAssetManifest): Promise<void> {
    await this.assetService.initializeGame(this.gameSlug, manifest);
    this.manifest = manifest || null;
  }

  /* --------------------------------------------------
   * TEXTURES
   * -------------------------------------------------- */

  async loadTexture(assetName: string): Promise<Texture> {
    return this.assetService.loadTexture(this.gameSlug, assetName);
  }

  getTexture(assetName: string): Texture | null {
    return this.assetService.getAsset<Texture>(
      this.gameSlug,
      'texture',
      assetName
    );
  }

  /* --------------------------------------------------
   * SPINE
   * -------------------------------------------------- */

  async loadSpine(spineName: string, scale?: number): Promise<Spine> {
    return this.assetService.loadSpine(this.gameSlug, spineName, { scale });
  }

  getSpine(spineName: string): Spine | null {
    return this.assetService.getAsset<Spine>(this.gameSlug, 'spine', spineName);
  }

  getSpineAnimations(spine: Spine): string[] {
    return this.assetService.getSpineAnimations(spine);
  }

  setSpineAnimation(
    spine: Spine,
    animationName: string,
    loop: boolean = true
  ): void {
    this.assetService.setSpineAnimation(spine, animationName, loop);
  }

  /* --------------------------------------------------
   * AUDIO
   * -------------------------------------------------- */

  async loadAudio(audioName: string): Promise<HTMLAudioElement> {
    return this.assetService.loadAudio(this.gameSlug, audioName);
  }

  getAudio(audioName: string): HTMLAudioElement | null {
    return this.assetService.getAsset<HTMLAudioElement>(
      this.gameSlug,
      'audio',
      audioName
    );
  }

  /* --------------------------------------------------
   * TEXTURE PACKER ATLASES
   * -------------------------------------------------- */

  /**
   * Load a TexturePacker atlas
   *
   * @param atlasName logical name (e.g. "background")
   * @param jsonPath path to atlas json
   * @param imagePath path to atlas png
   */
  async loadAtlas(
    atlasName: string,
    jsonPath: string,
    imagePath: string
  ): Promise<Spritesheet> {
    if (this.atlases.has(atlasName)) {
      return this.atlases.get(atlasName)!;
    }

    const sheet = await this.assetService.loadAtlas(
      this.gameSlug,
      atlasName,
      jsonPath,
      imagePath,
      { cache: true }
    );

    this.atlases.set(atlasName, sheet);
    return sheet;
  }

  /**
   * Get cached atlas
   */
  getAtlas(atlasName: string): Spritesheet | null {
    return this.atlases.get(atlasName) || null;
  }

  /* --------------------------------------------------
   * PRELOAD
   * -------------------------------------------------- */

  async preloadAll(): Promise<void> {
    if (!this.manifest) {
      console.warn(`[PixiBundler] No manifest provided for preload`);
      return;
    }

    await this.assetService.preloadGameAssets(this.gameSlug, this.manifest);

    // Preload atlases (if defined)
    if (this.manifest.assets.atlases) {
      const entries = Object.entries(this.manifest.assets.atlases);
      for (const [name, atlas] of entries) {
        await this.loadAtlas(name, atlas.json, atlas.image).catch(err =>
          console.warn(`[PixiBundler] Failed to preload atlas ${name}`, err)
        );
      }
    }
  }

  /* --------------------------------------------------
   * CLEANUP
   * -------------------------------------------------- */

  clearAssets(): void {
    this.atlases.clear();
    this.assetService.clearGameAssets(this.gameSlug);
  }
}

/* ----------------------------------------------------
 * FACTORY
 * ---------------------------------------------------- */

export function createPixiBundler(
  gameSlug: string,
  baseUrl?: string
): PixiBundler {
  return new PixiBundler(gameSlug, baseUrl);
}

/* ----------------------------------------------------
 * CONVENIENCE LOADER
 * ---------------------------------------------------- */

export async function loadAsset<T = any>(
  gameSlug: string,
  assetType: 'texture' | 'spine' | 'audio',
  assetName: string,
  baseUrl?: string
): Promise<T> {
  const bundler = createPixiBundler(gameSlug, baseUrl);
  await bundler.initialize();

  switch (assetType) {
    case 'texture':
      return bundler.loadTexture(assetName) as Promise<T>;
    case 'spine':
      return bundler.loadSpine(assetName) as Promise<T>;
    case 'audio':
      return bundler.loadAudio(assetName) as Promise<T>;
    default:
      throw new Error(`Unknown asset type: ${assetType}`);
  }
}
